import { inject, Injectable, Signal } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { createSelector, Store } from '@ngrx/store';
import type { Observable } from 'rxjs';

import { UserSelectors } from '@core/account-data';
import { filterBoolean } from '@core/common-legacy';
import { DepartmentSelectors } from '@core/department-data';
import { memoizeSelector } from '@core/ngrx-legacy';
import { OfferStatus } from '@core/offers-types';
import type { TalentPool, TalentPoolBrief, TalentPoolFull } from '@core/talent-pools-types';

import { TalentPoolsBriefSelectors } from './talent-pools/talent-pools-brief/talent-pools-brief.store';
import {
  TalentPoolsFullActions,
  TalentPoolsFullSelectors,
} from './talent-pools/talent-pools-full/talent-pools-full.store';
import {
  TalentPoolsItemActions,
  TalentPoolsItemSelectors,
} from './talent-pools/talent-pools-item/talent-pools-item.store';
import { TalentPoolsScope } from './talent-pools/types';

const getScope = (archived = false) =>
  archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default;

const talentPoolAllItems = createSelector(
  TalentPoolsItemSelectors.allEntities,
  DepartmentSelectors.entities(),
  UserSelectors.entities(),
  (entities, departments, users) =>
    entities.map(entity => {
      const department = departments.find(department => department.id === entity.departmentId);
      return {
        ...entity,
        department: department ? department.name : null,
      };
    }),
);
const talentPoolBriefByIdOrSLug = memoizeSelector((idOrSlug: number | string) =>
  createSelector(TalentPoolsBriefSelectors.allEntities, entities =>
    entities.find(entity => entity.slug === idOrSlug || entity.id === idOrSlug),
  ),
);
const talentPoolItems = memoizeSelector((archived: boolean = false) =>
  createSelector(talentPoolAllItems, entities =>
    entities.filter(
      entity => entity.status === (archived ? OfferStatus.Archived : OfferStatus.Draft),
    ),
  ),
);
const talentPoolItemByIdOrSLug = memoizeSelector((idOrSlug: number | string) =>
  createSelector(talentPoolAllItems, entities =>
    entities.find(entity => entity.slug === idOrSlug || entity.id === idOrSlug),
  ),
);
const talentPoolFullByIdOrSLug = memoizeSelector((idOrSlug: number | string) =>
  createSelector(
    TalentPoolsFullSelectors.byIdOrSlug(idOrSlug),
    DepartmentSelectors.entities(),
    (entity, departments) => {
      if (!entity) {
        return entity;
      }
      const department = departments.find(department => department.id === entity.departmentId);
      return {
        ...entity,
        department: department ? department.name : null,
      };
    },
  ),
);

const collectionScoped = memoizeSelector((archived: boolean = false) =>
  createSelector(talentPoolItems(archived), talentPools =>
    talentPools.sort((a, b) => b.position - a.position),
  ),
);
const collectionMapScoped = memoizeSelector((archived: boolean = false) =>
  createSelector(talentPoolItems(archived), talentPools =>
    talentPools.reduce(
      (acc, entity) => {
        acc[entity.id] = entity;
        return acc;
      },
      <Record<number, TalentPool>>{},
    ),
  ),
);
const collectionBriefScoped = memoizeSelector((archived: boolean) =>
  createSelector(
    TalentPoolsBriefSelectors.entities({
      scope: archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default,
    }),
    talentPools => talentPools.sort((a, b) => b.position - a.position),
  ),
);

@Injectable({
  providedIn: 'root',
})
export class TalentPoolsDataService {
  private store = inject(Store);
  private actions$ = inject(Actions);

  public collection$(archived: boolean = false): Observable<TalentPool[]> {
    return this.store.select(collectionScoped(archived)).pipe(filterBoolean);
  }

  public collectionBrief$(archived: boolean = false): Observable<TalentPoolBrief[]> {
    return this.store.select(collectionBriefScoped(archived)).pipe(filterBoolean);
  }

  public collectionMapSignal(archived: boolean = false): Signal<Record<number, TalentPool>> {
    return this.store.selectSignal(collectionMapScoped(archived));
  }

  public meta$(archived = false) {
    return this.store.select(TalentPoolsItemSelectors.meta({ scope: getScope(archived) }));
  }

  public entityMeta$(idOrSlug: number | string) {
    return this.store.select(TalentPoolsFullSelectors.metaByIdOrSlug(idOrSlug));
  }

  public briefEntityByIdOrSlug$(
    idOrSlug: number | string,
  ): Observable<TalentPoolBrief | undefined> {
    return this.store.select(talentPoolBriefByIdOrSLug(idOrSlug));
  }

  public briefEntitiesByIds$(ids: number[]): Observable<TalentPoolBrief[]> {
    return this.store.select(TalentPoolsBriefSelectors.entitiesByIds(ids));
  }

  public entityByIdOrSlug$(idOrSlug: number | string): Observable<TalentPool | undefined> {
    return this.store.select(talentPoolItemByIdOrSLug(idOrSlug));
  }

  public fullEntityByIdOrSlug$(idOrSlug: number | string): Observable<TalentPoolFull | undefined> {
    return this.store.select(talentPoolFullByIdOrSLug(idOrSlug));
  }

  public fetchCollection(archived: boolean = false): void {
    const scope: TalentPoolsScope = archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default;
    this.store.dispatch(TalentPoolsItemActions.fetchCollection({ payload: { scope } }));
  }

  public fetchBriefCollection(): void {
    this.store.dispatch(
      TalentPoolsItemActions.fetchCollection({
        payload: { scope: TalentPoolsScope.Archived },
      }),
    );
  }

  public fetchOne(idOrSlug: number | string): void {
    const payload = { id: idOrSlug };
    this.store.dispatch(TalentPoolsFullActions.fetchOne({ payload }));
  }

  public getFetchOneSuccessAction(): Actions<{
    payload: { id: number; offer?: { id: number }; talentPool?: { id: number } };
  }> {
    return this.actions$.pipe(ofType(TalentPoolsFullActions.fetchOneSuccess));
  }

  public getFetchOneFailedAction(): Actions<{
    payload: { id: number; offer?: { id: number }; talentPool?: { id: number } };
  }> {
    return this.actions$.pipe(ofType(TalentPoolsFullActions.fetchOneFailed));
  }
}
